import React, { useState, useEffect, useRef } from 'react';
import { apiRequest } from '../../api/index';
import images from '../../images';
import Pagination from '../Pagination';

const ProfileBillingTable = ({loading: loadingMain, userData, createdFrom, createdTo, filterType, onInvoiceLoaded, onDownload, onUpload }) => {
  const [invoices, setInvoices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(loadingMain);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [itemsLimit, setItemsLimit] = useState(20);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const fileInputRefs = useRef({});
  const [uploadingInvoiceId, setUploadingInvoiceId] = useState(null);

  const loadInvoices = async () => {
    if (!userData?.id) {
      // console.warn('ProfileBillingTable: userData.id не доступен для загрузки счетов');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      params.append('rekl_id', userData.id);
      params.append('limit', itemsLimit);
      params.append('offset', (currentPage - 1) * itemsLimit);
      
      if (createdFrom) {
        const fromDateTime = new Date(createdFrom);
        fromDateTime.setUTCHours(0, 0, 0, 0);
        params.append('created_from', fromDateTime.toISOString());
      }
      if (createdTo) {
        const toDateTime = new Date(createdTo);
        toDateTime.setUTCHours(23, 59, 59, 999);
        params.append('created_to', toDateTime.toISOString());
      }

      // Передаем has_lead в зависимости от filterType
      if (filterType === 'leads') {
        params.append('has_lead', 'true');
      } else if (filterType === 'invoices') {
        params.append('has_lead', 'false');
      }
      
      const response = await apiRequest(`invoice/list?${params.toString()}`, 'GET');
      
      const data = response.data;
      setTotal(data.total);

      setInvoices(data.invoices || []);

      if (onInvoiceLoaded) {
        onInvoiceLoaded(data.invoices || []);
      }
      
    } catch (error) {
      console.error('Ошибка при загрузке счетов:', error);
      setError(error.message || 'Не удалось загрузить счета');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [currentPage, createdFrom, createdTo, filterType, userData]);

  const handleStatusUpdate = async (invoiceId, currentStatus) => {
    try {
      setUpdatingStatusId(invoiceId);
      
      const newStatus = !currentStatus;
      const formData = new FormData();
      formData.append('status', newStatus);
      await apiRequest(`invoice/${invoiceId}/update`, 'PATCH', formData);
      
      await loadInvoices();
      
    } catch (error) {
      console.error('Ошибка при обновлении статуса:', error);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleFileUpload = async (event, invoiceId) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Проверяем, что файл имеет расширение .pdf
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Допускаются только PDF файлы!');
      return;
    }

    try {
      setUploadingInvoiceId(invoiceId);
      
      const formData = new FormData();
      formData.append('score', file);
      
      await apiRequest(`invoice/${invoiceId}/update`, 'PATCH', formData);
      
      await loadInvoices();
      
    } catch (error) {
      console.error('Ошибка при загрузке файла:', error);
    } finally {
      setUploadingInvoiceId(null);
    }
  };

  const handleUploadClick = (invoiceId) => {
    if (fileInputRefs.current[invoiceId]) {
      fileInputRefs.current[invoiceId].click();
    }
  };

  const handleDownload = async (orderId, fileName) => {
    try {
      const response = await apiRequest(`/files/${orderId}/download`, 'GET', null, true);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      // Открываем в новой вкладке
      window.open(url, '_blank');
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
    }
  };

  const handleDownloadInvoice = async (orderId, fileName) => {
    try {
      const response = await apiRequest(`invoice/document?invoice_id=${orderId}`, 'GET', null, true);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      // Открываем в новой вкладке
      window.open(url, '_blank');
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
    }
  };

  return (
    <>
      <div className="profile-billing-table-container">
        <table className="profile-billing-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Статус</th>
              <th>Сумма</th>
              <th>Дата</th>
              <th>Счет</th>
              <th>Поручение</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{textAlign: 'center', padding: '20px'}}>Загрузка счетов...</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="6" style={{textAlign: 'center', padding: '20px', color: 'red'}}>{error}</td>
              </tr>
            ) : invoices.length > 0 ? (
              invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td>{invoice.id}</td>
                  <td>
                    { invoice.lead_id ? (
                        <span className="status-badge">
                      Лид
                    </span>
                    ) :
                        (
                            <button
                                onClick={() => handleStatusUpdate(invoice.id, invoice.status)}
                                disabled={updatingStatusId === invoice.id}
                                className={invoice.status ? 'status-paid' : 'status-unpaid'}
                            >
                              {updatingStatusId === invoice.id ? 'Обновление...' :
                                      invoice.status ? 'Оплачен' : 'Не оплачен'}
                            </button>
                        )
                    }
                  </td>
                  <td>{new Intl.NumberFormat('ru-RU').format(invoice.paid)} ₽</td>
                  <td>{new Date(invoice.date_operation).toLocaleDateString('ru-RU')}</td>
                  <td>
                    {!invoice.lead_id && (
                        <>
                          {invoice.document ? (
                              <button
                                  className="download-button"
                                  onClick={() => handleDownloadInvoice(invoice.id, invoice.document_filename)}
                              >
                                <img 
                                  src={images.downloadLinear} 
                                  alt="Скачать" 
                                  style={{width: '16px', height: '16px'}} 
                                />
                                Скачать
                              </button>
                            ) : (
                          <></>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    {!invoice.lead_id && (
                      <>
                        {!invoice.score ? (
                            <button
                                className="upload-button"
                                onClick={() => handleUploadClick(invoice.id)}
                            >
                              <input
                                  type="file"
                                  style={{display: 'none'}}
                                  onChange={(e) => handleFileUpload(e, invoice.id)}
                                  disabled={uploadingInvoiceId === invoice.id}
                                  ref={(input) => {
                                    fileInputRefs.current[invoice.id] = input;
                                  }}
                              />
                              <img
                                  src={images.paperclipBoldActive}
                                  alt="Загрузить"
                                  style={{width: '16px', height: '16px'}}
                              />
                              {uploadingInvoiceId === invoice.id ? 'Загрузка...' : 'Загрузить'}
                            </button>
                        ) : (
                            <button
                                className="download-button"
                                onClick={() => handleDownload(invoice.score, `poruchenie_${invoice.id}.pdf`)}
                            >
                              <img
                                  src={images.documentLinear}
                                  alt="Поручение"
                                  style={{width: '16px', height: '16px'}}
                              />
                              Поручение
                            </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))
            ) : (
                <tr>
                  <td colSpan="6" style={{textAlign: 'center', padding: '20px'}}>Нет операций</td>
                </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        total={total}
        limit={itemsLimit}
        onPageChange={setCurrentPage}
        onLimitChange={setItemsLimit}
      />
    </>
  );
};

export default ProfileBillingTable;
